import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { first } from 'rxjs/operators';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { LanguageAttributeComponent } from './components/language-attribute/language-attribute.component';
import { CookieKey } from './models/cookie-key.model';
import { AppConfigService } from './services/app-config-service/app-config.service';
import { AutoLogoutServiceService } from './services/auto-logout-service/auto-logout-service.service';
import { SpinnerOverlayService } from './services/spinner-overlay/spinner-overlay.service';
import { ThemingService } from './services/theming-service/theming.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SpinnerOverlayComponent } from './components/spinner-overlay/spinner-overlay.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    TranslateModule,
    HeaderComponent,
    FooterComponent,
    LanguageAttributeComponent,
    SpinnerOverlayComponent,
    RouterModule,
    CommonModule,
  ],
})
export class AppComponent implements OnInit {
  areTranslationsAvailable: boolean = false;
  // to comply with keyboard navigation accessibility requirements when a dialog is opened, all elements not postioned in the dialog itself should not be focusable
  // this can be removed once ng-bootstrap fixes its failed focus-trapping in dialogs
  disableFocusOnBackgroundElements: boolean = false;

  constructor(
    private readonly translate: TranslateService,
    private readonly appConfigService: AppConfigService,
    private readonly themingService: ThemingService,
    private readonly cookieService: CookieService,
    private readonly autoLogoutService: AutoLogoutServiceService,
    private readonly router: Router,
    private readonly modalService: NgbModal,
    public spinnerOverlayService: SpinnerOverlayService
  ) {
    this.translate.onDefaultLangChange.pipe(first()).subscribe(() => (this.areTranslationsAvailable = true));

    this.themingService.loadSupportedTranslations().subscribe(langs => {
      translate.addLangs(langs);
      translate.setDefaultLang(this.appConfigService.configData.defaultLanguage);

      this.autoLogoutService.initAutoLogoutDetection();
      let lang: string = this.determineLanguage(langs);
      cookieService.set(CookieKey.Lang, lang);
      translate.use(lang);
    });
  }

  ngOnInit(): void {
    this.router.events.subscribe(event => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });

    this.setFaviconFromConfig();

    this.modalService.activeInstances.subscribe(instances => {
      this.disableFocusOnBackgroundElements = !!instances?.length;
    });
  }

  determineLanguage(langs: string[]): string {
    if (this.cookieService.check(CookieKey.Lang) && langs.includes(this.cookieService.get(CookieKey.Lang))) {
      return this.cookieService.get(CookieKey.Lang);
    }

    return langs.includes(this.translate.getBrowserLang()) ? this.translate.getBrowserLang() : this.translate.getDefaultLang();
  }

  private setFaviconFromConfig(): void {
    const faviconPath = this.themingService.getFaviconPath();
    if (faviconPath) {
      const favicon: HTMLLinkElement = document.querySelector('#re-favicon');

      favicon.href = faviconPath;
    }
  }
}
